import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, output } from '@angular/core';

import { CultureComponent, EmptyComponent, ErrorComponent, LoaderComponent, TextComponent } from '@camelot/ui';
import { CamBaseComponent, Culture, extractEnum } from '@camelot/utils';

import { AbstractCellComponent } from '../../../../grid/components/abstract';
import { AgGridComponent } from '../../../../grid/components/ag-grid/ag-grid.component';
import { GridControlComponent } from '../../../../grid/components/grid-control/grid-control.component';
import { GridTagComponent } from '../../../../grid/components/grid-tag/grid-tag.component';
import { GridBoolCellComponent } from '../../../../grid/components/templates/bool/bool.component';
import { CamGridData } from '../../../../grid/models/grid-data';
import { IFieldsOverrides } from '../../../../grid/models/types';
import { CamGridDataService } from '../../../../grid/services/grid-data.service';
import { CommunicationType } from '../../../services/dto/communication';
import { Template, TemplateVariant } from '../../../services/dto/template';
import { CamCommunicationsTemplatesService } from '../../../services/templates.service';
import { CommunicationTypeComponent } from '../../communication-type/communication-type.component';

@Component({
  imports: [
    LoaderComponent,
    ErrorComponent,
    AsyncPipe,
    EmptyComponent,
    AgGridComponent,
    GridTagComponent,
    GridControlComponent,
  ],
  selector: 'cam-communication-template-list',
  styleUrls: ['./list.component.scss'],
  templateUrl: './list.component.html',
})
export class CommunicationTemplateListComponent extends CamBaseComponent {
  askNavigation = output<string>();

  readonly id = 'communicationTemplateList';

  readonly communicationsTemplateService = inject(CamCommunicationsTemplatesService);
  readonly list$ = this.communicationsTemplateService.communicationTemplates.get$();

  readonly fieldsOverrides: IFieldsOverrides = {
    availableCultures: { cellRenderer: CultureCellComponent },
    communicationType: {
      colOptions: {
        enumInfo: {
          cellRenderer: CommunicationTypeCellComponent,
          enum: CommunicationType,
          values: extractEnum(CommunicationType, true).map(({ name, value }) => ({
            id: value.toString(),
            name: `communication.type.${name.toLocaleLowerCase()}`,
          })),
        },
      },
    },
    isActive: { cellRenderer: GridBoolCellComponent },
    variants: { cellRenderer: VariantsCellComponent },
  };

  protected _grid: CamGridData<Template> | null = null;

  private readonly _dataService = inject(CamGridDataService<Template>);

  constructor() {
    super();
    this._fetch();
  }

  ngOnInit() {
    this._grid = this._dataService.get(this.id, true);
    this._grid.switchView('grid');
    this._registerSubscription(this._grid.clickedRow$.subscribe(template => this.goToTemplate(template.id)));
  }

  public goToTemplate(id: string) {
    this.askNavigation.emit(id);
  }
  private _fetch() {
    this.communicationsTemplateService.fetchConversationTemplates$().subscribe({
      complete: () => {
        this.requestState.completed();
      },
      error: (error: HttpErrorResponse) => this.requestState.onError(error.status, error.statusText),
    });
  }
}

@Component({
  host: { 'data-cid': 'culture-cell' },
  imports: [CultureComponent],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '',
  template: `<div class="align-center" style="height: 100%">
    <cam-culture [cultures]="this.availableCultures"></cam-culture>
  </div>`,
})
export class CultureCellComponent extends AbstractCellComponent<any, Culture[]> {
  get availableCultures() {
    return this.value ?? [];
  }
}

@Component({
  host: { 'data-cid': 'variants-cell' },
  imports: [TextComponent],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '',
  template: `<div class="align-center" style="height: 100%">
    <cam-text>{{ this.title }}</cam-text>
  </div>`,
})
export class VariantsCellComponent extends AbstractCellComponent<any, TemplateVariant[]> {
  get body() {
    if (!this.value || this.value.length === 0) {
      return '';
    }
    return this.value[0].body ?? '';
  }
  get title() {
    if (!this.value || this.value.length === 0) {
      return '';
    }
    return this.value[0].title ?? '';
  }
}

@Component({
  host: { 'data-cid': 'communication-type-cell' },
  imports: [CommunicationTypeComponent],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '',
  template: `<div class="align-center" style="height: 100%">
    <cam-communication-type [value]="this.type"></cam-communication-type>
  </div>`,
})
export class CommunicationTypeCellComponent extends AbstractCellComponent<any, CommunicationType> {
  get type() {
    return this.value ?? CommunicationType.Unknown;
  }
}
